// src/lib/format.js
//
// ONE WAY TO WRITE A DATE AND ONE WAY TO WRITE A DOLLAR. #417 decision D32,
// which settles #544 and #35 together, approved by Colby on Sep 14, 2026.
// Built at Session 79.15b.
//
// THE MODULE SHIPS NOW; THE SWEEP OF ALL 15 CALL SITES IS SESSION 79.16. That
// split is #532's precedent and is deliberate — sweeping the call sites before
// the standard exists means sweeping them twice.
//
// ─────────────────────────────────────────────────────────────────────────────
// WHY, because the count is the argument: `fmtDate` and `fmt$` are reimplemented
// 22 times across 15 files, and the versions disagree in ways that matter.
//
//   · ELEVEN sites pass `undefined` as the locale, which means they render in
//     whatever the reader's computer is set to. A date is part of a purchase
//     record; it does not change shape because somebody's laptop is set to
//     en-GB.
//   · ONE of the fifteen renders a missing date as nothing at all, so a blank
//     cell means both "no date" and "we did not load it".
//   · ONE of the fifteen — InvoicingPage.js — carries the local-noon rule below,
//     derived from a live money-path defect. THE OTHER FOURTEEN DO NOT.
//   · And `money(null)` prints `$0.00` on Invoicing, whose entire subject is
//     whether a number is known yet.
// ─────────────────────────────────────────────────────────────────────────────

// D32 — LOCALE IS PINNED. Not `undefined`, which defers to the reader's machine.
const LOCALE = 'en-US';

// The em dash is the app's existing convention for "no value", already used by
// the shared `fmt$` and `fmtDate` in utils.js. It is NOT an empty string: a blank
// cell is indistinguishable from a cell that failed to load.
export const NONE = '—';

// ─────────────────────────────────────────────────────────────────────────────
// THE LOCAL-NOON RULE, and it is the one piece of this module that was paid for
// in a real defect rather than reasoned out.
//
// `invoice_date`, `due_date` and every other DATE column come back from PostgREST
// as a bare 'YYYY-MM-DD' with no zone. `new Date('2026-08-20')` parses that as
// UTC MIDNIGHT, which then renders in local time as ONE DAY EARLIER everywhere
// west of Greenwich — which is everywhere D&R operates.
//
// Found by live test at Session 74z, not by reading: an invoice entered as Aug 20
// with Net 30 rendered "Aug 19 · due Sep 18" against a sheet that had just
// computed Sep 19. Both dates feed AP aging and days-payable-outstanding, so a
// silent one-day shift is a money-path defect rather than a display quirk.
//
// THE REGEX IS WHAT KEEPS IT SAFE: anything already carrying a time component is
// already zoned and must NOT have one bolted on.
// ─────────────────────────────────────────────────────────────────────────────
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

function toDate(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const d = (typeof value === 'string' && DATE_ONLY.test(value))
    ? new Date(value + 'T12:00:00')
    : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

// D32 — FOUR NAMED SHAPES AND NO FIFTH. Named for where they are used rather
// than for what they contain, so a caller picks by asking "what am I building"
// rather than by remembering a format string.

// `list` — "Sep 14". For a column or a row where the year is obvious from
// context and the space is tight.
export function dateList(value) {
  const d = toDate(value);
  if (!d) return NONE;
  return d.toLocaleDateString(LOCALE, { month: 'short', day: 'numeric' });
}

// `full` — "Sep 14, 2026". For anything a person might read months later, or
// print, or quote to a vendor.
export function dateFull(value) {
  const d = toDate(value);
  if (!d) return NONE;
  return d.toLocaleDateString(LOCALE, { month: 'short', day: 'numeric', year: 'numeric' });
}

// `stamp` — "Sep 14 · 3:05 PM". For an audit trail, where the time is the point.
// The middot is the app's existing separator, not a comma.
export function dateStamp(value) {
  const d = toDate(value);
  if (!d) return NONE;
  const day = d.toLocaleDateString(LOCALE, { month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString(LOCALE, { hour: 'numeric', minute: '2-digit' });
  return `${day} · ${time}`;
}

// `relative` — "today", "yesterday", "3 days ago", and beyond seven days it hands
// off to `list`. D32 caps it at seven days deliberately: past that, "23 days ago"
// is arithmetic a reader has to undo to get back to the date they actually want.
//
// The comparison is made on CALENDAR DAYS rather than on elapsed milliseconds.
// 23:00 yesterday to 01:00 today is two hours and is still "yesterday", and a
// millisecond count calls it "today", which is the answer nobody means.
export function dateRelative(value, now = new Date()) {
  const d = toDate(value);
  if (!d) return NONE;
  const startOf = x => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(now) - startOf(d)) / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days > 1 && days < 7) return `${days} days ago`;
  if (days === -1) return 'tomorrow';
  if (days < -1 && days > -7) return `in ${Math.abs(days)} days`;
  return dateList(d);
}

// ─────────────────────────────────────────────────────────────────────────────
// THE SAME RULE ON THE WAY OUT — #596, Session 79.21b.
//
// A date the app FILLS IN for a person — a credit's date, a "needed by" button —
// must be the LOCAL calendar day. `new Date().toISOString().slice(0, 10)` is the
// UTC day, which from about 7pm Central (6pm in winter) until midnight is already
// tomorrow. The local-noon rule above stops a date being READ a day early; this
// stops one being WRITTEN a day late.
//
// 'YYYY-MM-DD' for today plus `days`, counted on the local calendar, so a
// daylight-saving change in between cannot move it.
// ─────────────────────────────────────────────────────────────────────────────
export function localDate(days = 0, now = new Date()) {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + (Number(days) || 0));
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// MONEY — and the null policy is the half with teeth.
//
// D32: "money(null) prints $0.00 on a screen whose entire subject is whether a
// number is known." Zero and unknown are different facts and the reader cannot
// tell them apart. An item with no cost on file is not an item that cost nothing,
// and a transfer valued at nothing is a real operational state this project has
// already met — Houston's made items travel at no standard cost until somebody
// prices the catalog.
// ─────────────────────────────────────────────────────────────────────────────
export function money(value) {
  if (value == null || value === '') return NONE;
  const n = Number(value);
  if (!Number.isFinite(n)) return NONE;
  return '$' + n.toLocaleString(LOCALE, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// A quantity is not money and must not carry a dollar sign, but it shares the
// null policy for the same reason: 0 on hand and "we have not counted" are
// different answers to the question the screen is asking.
export function quantity(value, { decimals = 0 } = {}) {
  if (value == null || value === '') return NONE;
  const n = Number(value);
  if (!Number.isFinite(n)) return NONE;
  return n.toLocaleString(LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}
